/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { classnames as cx, propsUtils } from '@ba-ui-toolkit/utils';
import HSpacer from '@ba-ui-toolkit/hspacer';
import Separator from '@ba-ui-toolkit/separator';
import ToolbarFactory from './ToolbarFactory';

import './Toolbar.scss';

const { sanitize } = propsUtils;

const Spacer = props => {
	const { size, ...passThrough } = props;
	let num = 2;
	switch (size) {
		case 'small':
			num = 1; // 8px
			break;
		case 'medium':
			num = 2; // 16px
			break;
		case 'large':
			num = 3; // 24px
			break;
		case 'extraLarge':
			num = 4; // 32px
			break;
		default:
	}

	return <HSpacer data-tid="ba-common-toolbar__h-spacer" size={num} {...passThrough} />;
};
Spacer.propTypes = {
	size: PropTypes.oneOf(['small', 'medium', 'large', 'extraLarge']),
};

const VSeparator = props => {
	const { size, ...passThrough } = props;
	let num;
	switch (size) {
		case 'none':
			num = 0; // 0px
			break;
		case 'small':
			num = 1; // 8px
			break;
		case 'medium':
			num = 2; // 16px
			break;
		case 'large':
			num = 3; // 24px
			break;
		case 'extraLarge':
			num = 4; // 32px
			break;
		default:
	}

	return (
		<Separator
			data-tid="ba-common-toolbar__v-separator"
			orientation="vertical"
			hMargin={num}
			{...passThrough}
		/>
	);
};
VSeparator.propTypes = {
	size: PropTypes.oneOf(['none', 'small', 'medium', 'large', 'extraLarge']),
};

VSeparator.isSeparator = true;

export default class Toolbar extends Component {
	static propTypes = {
		align: PropTypes.oneOf(['right', 'left', 'center']),
		children: PropTypes.node.isRequired,
		gap: PropTypes.oneOf(['none', 'small', 'medium', 'large', 'extraLarge']),
		border: PropTypes.bool,
		/** Align options are disabled under inline mode. Only select align options when variant is frame */
		variant: PropTypes.oneOf(['frame', 'inline']),
		/** Custom class name(s) */
		className: PropTypes.string,
		/** Custom css styles */
		style: PropTypes.object,
		/** Specifies whether the flexible items should wrap or not */
		flexWrap: PropTypes.oneOf(['wrap', 'nowrap', 'wrap-reverse']),
		/** Specifies whether the toolbar will be drawn horizontally or vertically */
		orientation: PropTypes.oneOf(['horizontal', 'vertical']),
	};

	static defaultProps = {
		align: 'left',
		gap: 'none',
		border: false,
		variant: 'inline',
		style: {},
		className: '',
		flexWrap: 'wrap',
		orientation: 'horizontal',
	};
	static LeftItems = ({ children }) => <span className={cx('itemLeft')}>{children}</span>;
	static RightItems = ({ children }) => <span className={cx('itemRight')}>{children}</span>;
	static Spacer = Spacer;
	static Separator = VSeparator;

	_isSeparator(component) {
		if (component && component.type && component.type.isSeparator) return true;

		return false;
	}

	render() {
		const {
			align,
			children,
			gap,
			border,
			variant,
			className,
			style,
			flexWrap,
			orientation,
			...others
		} = this.props;
		const classNames = cx('ba-common-toolbar', align, variant, orientation, {
			[`gap-${gap}`]: gap,
			border,
		});

		return (
			<div
				{...sanitize(others, Toolbar)}
				className={`${classNames} ${className}`}
				style={{ flexWrap, ...style }}
			>
				{React.Children.map(children, (child, i) => (
					<div
						className={`ba-common-toolbar__item ${
							this._isSeparator(child) ? cx('separator') : ''
						}`}
						key={i}
						onKeyDown={e => {
							if (e.keyCode === 13) {
								e.preventDefault();
								e.stopPropagation();
								if (child.props.onClick) child.props.onClick();
							}
						}}
					>
						{child}
					</div>
				))}
			</div>
		);
	}
}

export { ToolbarFactory };
